import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, ScanSearch } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { VerDialog, VerRow, VerTiles } from "@/components/v52/VerParts";
import { useT52 } from "@/i18n/v52";

const TABS = ["pending", "confirmed", "rejected", "all"];
const PAGE = 50;
const errMsg = (e, fb) => e?.response?.data?.detail?.message || fb;

function Toolbar({ tab, setTab, q, setQ, total }) {
  const { t52 } = useT52();
  return (
    <div className="f52-ver-bar">
      <div className="f52-ver-tabs" role="tablist">
        {TABS.map((x) => (
          <button key={x} type="button" role="tab" aria-selected={tab === x} className={`f52-btn ${tab === x ? "f52-btn-primary" : "f52-btn-secondary"}`}
            onClick={() => setTab(x)} data-testid={`ver-tab-${x}`}>{t52(`ver_tab_${x}`)}</button>
        ))}
      </div>
      <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t52("ver_search")} aria-label={t52("ver_search")}
        className="f52-ver-search" data-testid="ver-search" />
      <span className="f52-imp-muted" data-testid="ver-total">{total === 1 ? t52("ver_count_one") : t52("ver_count", { n: total })}</span>
    </div>
  );
}

export default function Verzeichnispruefung() {
  const { t52 } = useT52();
  const { user } = useAuth();
  const [sum, setSum] = useState(null);
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [list, setList] = useState({ total: 0, items: [] });
  const [dlg, setDlg] = useState(null);
  const seq = useRef(0);
  const loadSum = useCallback(() => api.get("/verification/summary").then((r) => setSum(r.data)).catch(() => setSum(false)), []);
  const loadList = useCallback((offset = 0) => {
    const n = ++seq.current;
    return api.get("/verification/rows", { params: { status: tab, q, offset, limit: PAGE } })
      .then((r) => { if (n === seq.current) setList((s) => ({ total: r.data.total, items: offset ? [...s.items, ...r.data.items] : r.data.items })); })
      .catch(() => { if (n === seq.current) setList({ total: 0, items: [], error: true }); });
  }, [tab, q]);
  useEffect(() => { loadSum(); }, [loadSum]);
  useEffect(() => {
    if (!sum?.auto_running) return undefined;
    const id = setInterval(loadSum, 10000);
    return () => clearInterval(id);
  }, [sum?.auto_running, loadSum]);
  useEffect(() => {
    const id = setTimeout(() => loadList(0), 250);
    return () => clearTimeout(id);
  }, [loadList]);

  const submit = async (v) => {
    try {
      await api.post(`/verification/rows/${dlg.row.row_id}/decision`, { decision: dlg.decision, ...v, reason: dlg.decision === "reject" ? v.reason : null });
      toast.success(t52("ver_saved"));
      setDlg(null);
      loadSum();
      loadList(0);
    } catch (e) {
      toast.error(errMsg(e, t52("ver_err")));
    }
  };
  const autoRun = () => api.post("/verification/auto-run").then(() => { toast.success(t52("ver_auto_started")); loadSum(); })
    .catch((e) => toast.error(errMsg(e, t52("ver_err"))));

  if (sum === null) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;
  const actions = (
    <div className="flex flex-wrap gap-2">
      <button type="button" onClick={() => { loadSum(); loadList(0); }} className="f52-btn f52-btn-secondary" data-testid="ver-refresh"><RefreshCw aria-hidden="true" />{t52("ver_refresh")}</button>
      {user?.role === "super_admin" && (
        <button type="button" onClick={autoRun} disabled={!!sum?.auto_running} className="f52-btn f52-btn-secondary" data-testid="ver-auto-run">
          <ScanSearch aria-hidden="true" />{t52(sum?.auto_running ? "ver_auto_busy" : "ver_auto_run")}
        </button>
      )}
    </div>
  );
  return (
    <div data-testid="ver-page" className="space-y-6">
      <PageHeader title={t52("ver_title")} subtitle={t52("ver_lead")}>{actions}</PageHeader>
      {sum === false ? <p className="f52-imp-warn" data-testid="ver-load-error">{t52("ver_load_err")}</p> : <VerTiles s={sum} />}
      <p className="f52-imp-muted" data-testid="ver-rule">{t52("ver_rule")}</p>
      <Panel testid="ver-list-panel">
        <Toolbar tab={tab} setTab={setTab} q={q} setQ={setQ} total={list.total} />
        {list.error ? <p className="f52-imp-warn" data-testid="ver-list-error">{t52("ver_load_err")}</p>
          : !list.items.length ? <p className="f52-imp-muted" data-testid="ver-empty">{t52("ver_empty")}</p>
            : <ul className="f52-imp-steps f52-ver-list">{list.items.map((r) => <VerRow key={r.row_id} r={r} onDecide={(row, decision) => setDlg({ row, decision })} />)}</ul>}
        {list.items.length < list.total && (
          <button type="button" className="f52-btn f52-btn-secondary" onClick={() => loadList(list.items.length)} data-testid="ver-more">{t52("ver_more")}</button>
        )}
      </Panel>
      {dlg && <VerDialog dlg={dlg} onCancel={() => setDlg(null)} onSubmit={submit} />}
    </div>
  );
}
